/**
 * AUTO-GENERATED. Do not edit.
 *
 * Source-string export of `worklets/granular.worklet.js`. Regenerated
 * on every build; consumers create a Blob URL from this string and pass
 * it to `engine.loadWorklet()` to register the AudioWorkletProcessor
 * without a separate file fetch.
 */
export const GRANULAR_WORKLET_SOURCE = `// Evil Synth — Granular Delay AudioWorklet processor.
//
// A delay line that doesn't replay the buffer linearly — instead it
// scatters short overlapping "grains" around a target read position,
// each one Hann-windowed, optionally pitched, optionally panned.
// The result is a smeary, granular tail rather than a clean echo.
//
// Signal flow per sample:
//
//   input[i] ──► circular buffer[writeHead]
//                       │
//                       │ (write head advances)
//                       ▼
//                grains read from buffer at writeHead - time*sampleRate
//                with random offset and pitched playback rate.
//                Output = sum of all active grain reads, Hann-enveloped,
//                normalized for expected overlap, soft-saturated.
//                The wet output is also fed back into the buffer with
//                gain \`feedback\`, so each pass through the loop becomes
//                progressively more granular.
//
// Stereo: per-channel buffers + per-grain pan. Mono input is fanned
// to both buffer channels so a mono synth still gets a stereo grain
// scatter through the spread parameter.

const BUFFER_SECONDS = 3.0;     // covers max delay of 2s with headroom
const MAX_GRAINS = 32;          // CPU sanity cap; concurrent grains beyond this drop

class GranularDelayProcessor extends AudioWorkletProcessor {
  static get parameterDescriptors() {
    return [
      { name: 'time',     defaultValue: 0.5,  minValue: 0.02, maxValue: 2.0,  automationRate: 'k-rate' },
      { name: 'feedback', defaultValue: 0.4,  minValue: 0,    maxValue: 0.95, automationRate: 'k-rate' },
      { name: 'size',     defaultValue: 0.15, minValue: 0.02, maxValue: 0.5,  automationRate: 'k-rate' },
      { name: 'density',  defaultValue: 12,   minValue: 1,    maxValue: 50,   automationRate: 'k-rate' },
      { name: 'spread',   defaultValue: 0.3,  minValue: 0,    maxValue: 1,    automationRate: 'k-rate' },
      { name: 'pitch',    defaultValue: 0,    minValue: -24,  maxValue: 24,   automationRate: 'k-rate' },
    ];
  }

  constructor() {
    super();
    this.bufferSize = Math.ceil(sampleRate * BUFFER_SECONDS);
    // Two channels' worth of circular buffer. We always allocate
    // stereo regardless of input channelCount — mono input fans into
    // both, stereo input writes channel-true.
    this.bufL = new Float32Array(this.bufferSize);
    this.bufR = new Float32Array(this.bufferSize);
    this.writeHead = 0;
    // Active grains. Each: { age, length, pos (read index, fractional),
    // rate (playback speed), gainL, gainR }. Plain object literals —
    // small allocation churn is acceptable at grain rates ≤ 50/sec
    // and keeps the code readable.
    this.grains = [];
    this.samplesUntilNextGrain = 0;
    // Track the last wet output so feedback can be re-injected one
    // sample later (a 1-sample delay in the FB loop is fine and
    // avoids trying to read the buffer position we're about to write).
    this.lastWetL = 0;
    this.lastWetR = 0;
  }

  spawnGrain(time, size, spread, pitch) {
    if (this.grains.length >= MAX_GRAINS) return;

    // Base read position — \`time\` seconds behind the current write
    // head, with a random offset proportional to spread. The offset
    // gives each grain a slightly different bite of the past, which
    // is what makes granular feel scattered rather than echoed.
    const baseSamples = time * sampleRate;
    const maxOffsetSec = spread * 0.4; // up to 400ms of jitter at full spread
    const offsetSamples = (Math.random() * 2 - 1) * maxOffsetSec * sampleRate;
    let basePos = this.writeHead - baseSamples + offsetSamples;
    // Wrap into [0, bufferSize). JS modulo can return negative; the
    // double-mod pattern guarantees a non-negative result.
    basePos = ((basePos % this.bufferSize) + this.bufferSize) % this.bufferSize;

    const lengthSamples = Math.max(1, Math.floor(size * sampleRate));
    const playbackRate = Math.pow(2, pitch / 12);

    // Random pan within ±spread. spread=0 → all grains center; spread=1
    // → grains can be fully L or fully R.
    const pan = (Math.random() * 2 - 1) * spread;
    // Linear pan-law (cheaper than constant-power, fine here since the
    // grain envelope dominates perceived loudness anyway).
    const gainL = pan <= 0 ? 1 : 1 - pan;
    const gainR = pan >= 0 ? 1 : 1 + pan;

    this.grains.push({
      age: 0,
      length: lengthSamples,
      pos: basePos,
      rate: playbackRate,
      gainL,
      gainR,
    });
  }

  process(inputs, outputs, parameters) {
    const input = inputs[0];
    const output = outputs[0];
    if (!output || output.length === 0) return true;

    // k-rate parameters — read once per render quantum.
    const time = parameters.time[0] ?? 0.5;
    const feedback = parameters.feedback[0] ?? 0.4;
    const size = parameters.size[0] ?? 0.15;
    const density = parameters.density[0] ?? 12;
    const spread = parameters.spread[0] ?? 0.3;
    const pitch = parameters.pitch[0] ?? 0;

    const samplesPerGrain = sampleRate / Math.max(1, density);

    // Resolve channel inputs/outputs. Mono input → fan to both
    // buffer channels and both outputs. Single-output (mono) writes
    // to outL only; stereo writes to both.
    const inL = input?.[0];
    const inR = input?.[1] ?? input?.[0];
    const outL = output[0];
    const outR = output[1] ?? null;

    const nFrames = outL ? outL.length : 0;
    const bufL = this.bufL;
    const bufR = this.bufR;
    const bufSize = this.bufferSize;

    // Pre-compute the per-pass overlap normalizer. With density × size
    // grains active on average (each Hann-windowed to 0.5 mean amplitude),
    // the sum naturally lands at roughly density*size*0.5 of the source
    // signal. Dividing by max(1, density*size) pulls it back near unity.
    const overlap = Math.max(1, density * size);
    const overlapInv = 1 / overlap;

    for (let i = 0; i < nFrames; i++) {
      // 1. Read input.
      const sampleL = inL ? (inL[i] ?? 0) : 0;
      const sampleR = inR ? (inR[i] ?? 0) : 0;

      // 2. Write input + feedback into buffer at the write head.
      bufL[this.writeHead] = sampleL + this.lastWetL * feedback;
      bufR[this.writeHead] = sampleR + this.lastWetR * feedback;

      // 3. Advance the write head (circular).
      this.writeHead = this.writeHead + 1;
      if (this.writeHead >= bufSize) this.writeHead = 0;

      // 4. Grain scheduler — fire a new grain when the inter-grain
      //    timer runs out. The +/- 20% spread-scaled jitter on the
      //    interval prevents the grain stream from sounding metronomic.
      this.samplesUntilNextGrain -= 1;
      if (this.samplesUntilNextGrain <= 0) {
        const pitchJitter = (Math.random() * 2 - 1) * spread * 12;
        this.spawnGrain(time, size, spread, pitch + pitchJitter);
        const intervalJitter = (Math.random() * 0.4 - 0.2) * spread;
        this.samplesUntilNextGrain = samplesPerGrain * (1 + intervalJitter);
      }

      // 5. Sum all active grains. Iterating in reverse so splice() on
      //    finished grains doesn't disturb the indices we haven't
      //    visited yet.
      let wetL = 0;
      let wetR = 0;
      for (let g = this.grains.length - 1; g >= 0; g--) {
        const grain = this.grains[g];

        // Hann window: 0.5 * (1 - cos(2π * t/length)). Smooth fade
        // in + out, no clicks at grain boundaries.
        const phase = grain.age / grain.length;
        const env = 0.5 * (1 - Math.cos(2 * Math.PI * phase));

        // Linear-interpolated read so fractional positions don't
        // step-quantize and zipper. idxH wraps at the buffer end so
        // reads near the boundary stay smooth.
        const idxL = Math.floor(grain.pos);
        const idxH = idxL + 1 >= bufSize ? 0 : idxL + 1;
        const frac = grain.pos - idxL;
        const sL = bufL[idxL] * (1 - frac) + bufL[idxH] * frac;
        const sR = bufR[idxL] * (1 - frac) + bufR[idxH] * frac;

        wetL += sL * env * grain.gainL;
        wetR += sR * env * grain.gainR;

        // Advance the grain's read position (wrapping) and age.
        grain.pos += grain.rate;
        if (grain.pos >= bufSize) grain.pos -= bufSize;
        else if (grain.pos < 0) grain.pos += bufSize;
        grain.age += 1;

        if (grain.age >= grain.length) {
          this.grains.splice(g, 1);
        }
      }

      // 6. Normalize for expected overlap, soft-saturate as a feedback
      //    safety net (large feedback + scattered grains can build to
      //    >1 amplitude; tanh keeps it from ever getting ear-piercing).
      wetL = Math.tanh(wetL * overlapInv);
      wetR = Math.tanh(wetR * overlapInv);

      this.lastWetL = wetL;
      this.lastWetR = wetR;

      outL[i] = wetL;
      if (outR) outR[i] = wetR;
    }

    return true;
  }
}

registerProcessor('granular-delay-processor', GranularDelayProcessor);
`;
